import React, { useEffect, useRef } from 'react';
import type { Task } from '../../../packages/api-client/pm-api';

/**
 * The task's objective. The API has no separate acceptance-criteria field, so
 * criteria live in the description text (intentional gap vs the concept's list).
 */
export function DescriptionField({ task, setTask, save, readOnly = false }: {
    task: Task;
    setTask: React.Dispatch<React.SetStateAction<Task | null>>;
    save: (patch: Partial<Task>) => Promise<void>;
    readOnly?: boolean;
}) {
    // Keep the description textarea sized to its content so the full
    // description is visible by default — no manual expand/scroll per view.
    const descRef = useRef<HTMLTextAreaElement>(null);
    const autoGrowDesc = (el: HTMLTextAreaElement) => {
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
    };

    // Re-fit the description box whenever the loaded task's description changes
    // (initial load, navigating between tasks, external updates).
    useEffect(() => {
        if (descRef.current) autoGrowDesc(descRef.current);
    }, [task?.description]);

    if (readOnly) return task.description
        ? <p className="task-objective-text">{task.description}</p>
        : <p className="task-muted">No objective written yet.</p>;

    return (
        <textarea
            ref={descRef}
            aria-label="Objective and acceptance criteria"
            className="task-description-input"
            placeholder="Add a description…"
            value={task.description}
            onChange={e => {
                autoGrowDesc(e.target);
                setTask(t => t ? { ...t, description: e.target.value } : t);
            }}
            onBlur={e => save({ description: e.target.value })}
        />
    );
}
